import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";

import { applyScrollerOffset, useMarqueeDrag } from "./-index.monthlyHiscoresMarquee.drag";

function useMarqueeOverflow(entryCount: number) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLUListElement>(null);
  const [overflows, setOverflows] = useState(false);
  const [autoScroll, setAutoScroll] = useState(false);

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => {
      const nextOverflows = track.scrollWidth > viewport.clientWidth + 1;
      setOverflows(nextOverflows);
      setAutoScroll(nextOverflows && !media.matches);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(viewport);
    observer.observe(track);
    media.addEventListener("change", update);
    return () => {
      observer.disconnect();
      media.removeEventListener("change", update);
    };
  }, [entryCount]);

  return { viewportRef, trackRef, overflows, autoScroll };
}

function isAutoScrollBlocked({
  dragActiveRef,
  hoverPausedRef,
  popoverPausedRef,
}: {
  dragActiveRef: { current: boolean };
  hoverPausedRef: { current: boolean };
  popoverPausedRef: { current: boolean };
}): boolean {
  return dragActiveRef.current || hoverPausedRef.current || popoverPausedRef.current;
}

function useMarqueeAutoScroll({
  scrollerRef,
  offsetRef,
  dragActiveRef,
  hoverPausedRef,
  popoverPausedRef,
  autoScroll,
  durationSeconds,
}: {
  scrollerRef: RefObject<HTMLDivElement | null>;
  offsetRef: { current: number };
  dragActiveRef: { current: boolean };
  hoverPausedRef: { current: boolean };
  popoverPausedRef: { current: boolean };
  autoScroll: boolean;
  durationSeconds: number;
}) {
  useEffect(() => {
    if (!autoScroll) return;

    let raf = 0;
    let last = performance.now();

    const tick = (now: number) => {
      const scroller = scrollerRef.current;
      if (scroller && !isAutoScrollBlocked({ dragActiveRef, hoverPausedRef, popoverPausedRef })) {
        const halfWidth = scroller.scrollWidth / 2;
        if (halfWidth > 0) {
          const delta = (now - last) * (halfWidth / (durationSeconds * 1000));
          applyScrollerOffset({ scroller, offsetRef, offset: offsetRef.current - delta });
        }
      }
      last = now;
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [
    autoScroll,
    durationSeconds,
    scrollerRef,
    offsetRef,
    dragActiveRef,
    hoverPausedRef,
    popoverPausedRef,
  ]);
}

function useMarqueeHoverPause(hoverPausedRef: RefObject<boolean>) {
  return {
    onMouseEnter: () => {
      if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
        hoverPausedRef.current = true;
      }
    },
    onMouseLeave: () => {
      hoverPausedRef.current = false;
    },
  };
}

type DraggableMarqueeInput = {
  entryCount: number;
  durationSeconds: number;
  popoverPaused?: boolean;
  onDragStart?: () => void;
};

function useMarqueePauseRefs(popoverPaused: boolean) {
  const hoverPausedRef = useRef(false);
  const popoverPausedRef = useRef(popoverPaused);
  const dragActiveRef = useRef(false);
  useLayoutEffect(() => {
    popoverPausedRef.current = popoverPaused;
  }, [popoverPaused]);
  return { hoverPausedRef, popoverPausedRef, dragActiveRef };
}

export function useDraggableMarquee({
  entryCount,
  durationSeconds,
  popoverPaused = false,
  onDragStart,
}: DraggableMarqueeInput) {
  const { viewportRef, trackRef, overflows, autoScroll } = useMarqueeOverflow(entryCount);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const { hoverPausedRef, popoverPausedRef, dragActiveRef } = useMarqueePauseRefs(popoverPaused);

  useMarqueeAutoScroll({
    scrollerRef,
    offsetRef,
    dragActiveRef,
    hoverPausedRef,
    popoverPausedRef,
    autoScroll,
    durationSeconds,
  });

  useEffect(() => {
    if (overflows) return;
    offsetRef.current = 0;
    const scroller = scrollerRef.current;
    if (scroller) scroller.style.transform = "";
  }, [overflows]);

  const drag = useMarqueeDrag({ overflows, scrollerRef, offsetRef, dragActiveRef, onDragStart });
  const hoverPause = useMarqueeHoverPause(hoverPausedRef);

  return {
    viewportRef,
    trackRef,
    scrollerRef,
    overflows,
    isDragging: drag.isDragging,
    scrollerProps: {
      onPointerDown: drag.onPointerDown,
      onPointerMove: drag.onPointerMove,
      onPointerUp: drag.onPointerUp,
      onPointerCancel: drag.onPointerCancel,
      onClickCapture: drag.onClickCapture,
      onMouseEnter: hoverPause.onMouseEnter,
      onMouseLeave: hoverPause.onMouseLeave,
    },
  };
}
